/**
 * Icons shared by the navigation rail, the chat list and the conversation panes.
 *
 * Each glyph comes from the shared icon library and is exported as a component so callers
 * keep a single import site and a fixed size per icon. The files draw with `currentColor`,
 * so one copy follows hover, the active state and the theme.
 */

import { ReactComponent as SvgAlertCircle } from '../../assets/images/icon/line/alert-circle.svg';
import { ReactComponent as SvgArchive } from '../../assets/images/icon/line/archive.svg';
import { ReactComponent as SvgArrowRight } from '../../assets/images/icon/line/arrow-right.svg';
import { ReactComponent as SvgCamera } from '../../assets/images/icon/line/camera.svg';
import { ReactComponent as SvgChevronDown } from '../../assets/images/icon/line/chevron-down.svg';
import { ReactComponent as SvgUsers } from '../../assets/images/icon/line/users.svg';
import { ReactComponent as SvgUserMinus } from '../../assets/images/icon/line/user-minus.svg';
import { ReactComponent as SvgBarChart } from '../../assets/images/icon/line/bar-chart-2.svg';
import { ReactComponent as SvgBell } from '../../assets/images/icon/line/bell.svg';
import { ReactComponent as SvgBlock } from '../../assets/images/icon/line/block.svg';
import { ReactComponent as SvgBulb } from '../../assets/images/icon/line/bulb.svg';
import { ReactComponent as SvgBookmark } from '../../assets/images/icon/line/bookmark.svg';
import { ReactComponent as SvgBookmarkOff } from '../../assets/images/icon/line/bookmark-off.svg';
import { ReactComponent as SvgCalendar } from '../../assets/images/icon/line/calendar.svg';
import { ReactComponent as SvgChat } from '../../assets/images/icon/line/chat-2.svg';
import { ReactComponent as SvgChevronLeft } from '../../assets/images/icon/line/chevron-left.svg';
import { ReactComponent as SvgChevronRight } from '../../assets/images/icon/line/chevron-right.svg';
import { ReactComponent as SvgClock } from '../../assets/images/icon/line/clock-hour-4.svg';
import { ReactComponent as SvgClose } from '../../assets/images/icon/line/close.svg';
import { ReactComponent as SvgColor } from '../../assets/images/icon/line/color.svg';
import { ReactComponent as SvgCopy } from '../../assets/images/icon/line/copy.svg';
import { ReactComponent as SvgCornerUpRight } from '../../assets/images/icon/line/corner-up-right.svg';
import { ReactComponent as SvgDownload } from '../../assets/images/icon/line/download.svg';
import { ReactComponent as SvgEraser } from '../../assets/images/icon/line/eraser.svg';
import { ReactComponent as SvgFile } from '../../assets/images/icon/line/file.svg';
import { ReactComponent as SvgFlag } from '../../assets/images/icon/line/flag.svg';
import { ReactComponent as SvgFlag2 } from '../../assets/images/icon/line/flag-2.svg';
import { ReactComponent as SvgGrid } from '../../assets/images/icon/line/grid.svg';
import { ReactComponent as SvgHome } from '../../assets/images/icon/line/home-4.svg';
import { ReactComponent as SvgImage } from '../../assets/images/icon/line/image.svg';
import { ReactComponent as SvgInfo } from '../../assets/images/icon/line/info.svg';
import { ReactComponent as SvgLink } from '../../assets/images/icon/line/link.svg';
import { ReactComponent as SvgLocation } from '../../assets/images/icon/line/location.svg';
import { ReactComponent as SvgLock } from '../../assets/images/icon/line/lock.svg';
import { ReactComponent as SvgLogOut } from '../../assets/images/icon/line/log-out.svg';
import { ReactComponent as SvgMapPin } from '../../assets/images/icon/line/map-pin.svg';
import { ReactComponent as SvgMenu } from '../../assets/images/icon/line/menu.svg';
import { ReactComponent as SvgGrip } from '../../assets/images/icon/line/Menu 2.svg';
import { ReactComponent as SvgMic } from '../../assets/images/icon/line/mic.svg';
import { ReactComponent as SvgMinus } from '../../assets/images/icon/line/minus.svg';
import { ReactComponent as SvgPhone } from '../../assets/images/icon/line/phone.svg';
import { ReactComponent as SvgPin } from '../../assets/images/icon/line/pin.svg';
import { ReactComponent as SvgPlus } from '../../assets/images/icon/line/plus.svg';
import { ReactComponent as SvgRadio } from '../../assets/images/icon/line/radio.svg';
import { ReactComponent as SvgReply } from '../../assets/images/Updated-image/reply (2).svg';
import { ReactComponent as SvgSearch } from '../../assets/images/icon/line/search.svg';
import { ReactComponent as SvgSend } from '../../assets/images/icon/line/send-1.svg';
import { ReactComponent as SvgSendClock } from '../../assets/images/icon/line/send-clock.svg';
import { ReactComponent as SvgSetting } from '../../assets/images/icon/line/setting.svg';
import { ReactComponent as SvgSmile } from '../../assets/images/icon/line/smile.svg';
import { ReactComponent as SvgStar } from '../../assets/images/icon/line/star.svg';
import { ReactComponent as SvgCheck } from '../../assets/images/icon/line/check.svg';
import { ReactComponent as SvgCheckDouble } from '../../assets/images/icon/line/check-double.svg';
import { ReactComponent as SvgDisc } from '../../assets/images/icon/line/disc.svg';
import { ReactComponent as SvgUnarchive } from '../../assets/images/icon/line/unarchive.svg';
import { ReactComponent as SvgBellOff } from '../../assets/images/icon/line/bell-off.svg';
import { ReactComponent as SvgTrash3 } from '../../assets/images/icon/line/trash-3.svg';
import { ReactComponent as SvgPencil } from '../../assets/images/icon/line/pencil.svg';
import { ReactComponent as SvgTextSize } from '../../assets/images/icon/line/text-size.svg';
import { ReactComponent as SvgTrash } from '../../assets/images/icon/line/trash.svg';
import { ReactComponent as SvgUnpin } from '../../assets/images/icon/line/unpin.svg';
import { ReactComponent as SvgUser } from '../../assets/images/icon/line/user.svg';
import { ReactComponent as SvgUserPlus } from '../../assets/images/icon/line/user-plus.svg';
import { ReactComponent as SvgVideo } from '../../assets/images/icon/line/video.svg';
import { ReactComponent as SvgMoreVertical } from '../../assets/images/icon/solid/more-vertical.svg';
import { ReactComponent as SvgPause } from '../../assets/images/icon/solid/pause.svg';
import { ReactComponent as SvgPlay } from '../../assets/images/icon/solid/play.svg';
import { ReactComponent as SvgStarOff } from '../../assets/images/icon/line/star-off.svg';
import { ReactComponent as SvgStop } from '../../assets/images/icon/solid/stop.svg';

/**
 * Wraps a library glyph at a fixed size.
 *
 * The size travels with the icon rather than with each caller, so the same glyph reused in
 * two places stays the size its screen was designed at. A caller may still add a class --
 * to colour the glyph for one row, say -- and it is appended to the icon's own.
 *
 * @param Svg The imported glyph component.
 * @param size Edge length in pixels.
 * @param className Class applied to the rendered svg.
 * @returns A component taking an optional className.
 */
const icon =
    (Svg, size, className) =>
    ({ className: extra } = {}) => (
        <Svg
            className={[className, extra].filter(Boolean).join(' ') || undefined}
            width={size}
            height={size}
            aria-hidden="true"
        />
    );

/* Navigation rail --------------------------------------------------------- */

// `side-nav-icon` is what common.scss sizes the rail glyphs by.
export const RailHomeIcon = icon(SvgHome, 22, 'side-nav-icon');
export const RailCallsIcon = icon(SvgPhone, 22, 'side-nav-icon');
export const RailChatIcon = icon(SvgChat, 22, 'side-nav-icon');
export const RailSettingsIcon = icon(SvgSetting, 22, 'side-nav-icon');

// The services tile is larger than the rest of the rail.
export const RailServicesIcon = icon(SvgGrid, 26, 'side-nav-icon');

export const RailLogoutIcon = icon(SvgLogOut, 22);

/* Chat list --------------------------------------------------------------- */

export const IconNewChat = icon(SvgPlus, 22);
export const IconOverflow = icon(SvgMoreVertical, 20);
export const IconLink = icon(SvgLink, 18);
export const IconCopy = icon(SvgCopy, 18);
export const IconChatTags = icon(SvgMenu, 16);
export const IconChevronRight = icon(SvgChevronRight, 16);
export const IconChevronLeft = icon(SvgChevronLeft, 20);
export const IconForwarded = icon(SvgCornerUpRight, 13);

/* Create chat menu and pop-ups (FR60) ------------------------------------- */

export const IconMenuNewContact = icon(SvgUserPlus, 19);
export const IconMenuNewGroup = icon(SvgUsers, 19);
export const IconSearchField = icon(SvgSearch, 18);
export const IconAddCircle = icon(SvgPlus, 16);
export const IconContinue = icon(SvgArrowRight, 18);
export const IconCamera = icon(SvgCamera, 34);
export const IconLogoEdit = icon(SvgPencil, 14);
export const IconCheckbox = icon(SvgCheck, 12);
export const IconChipRemove = icon(SvgClose, 14);
export const IconMemberRemove = icon(SvgClose, 16);
export const IconSelectArrow = icon(SvgChevronDown, 17);
// Same glyph and size the auth screens drew beside the dial code.
export const IconCountryChevron = icon(SvgChevronDown, 16);

/* Contact card and profile (FR71) ----------------------------------------- */

export const IconBlock = icon(SvgBlock, 18);
export const IconInfo = icon(SvgInfo, 15);
export const IconPhone = icon(SvgPhone, 19);
export const IconVideoCam = icon(SvgVideo, 15);
export const IconUserPlus = icon(SvgUserPlus, 14);
export const IconContact = icon(SvgUser, 15);
export const IconMessageBubble = icon(SvgChat, 18);

/* Composer (FR71) ---------------------------------------------------------- */

export const IconSchedule = icon(SvgCalendar, 21);

/* Scheduled messages (FR193–FR196). */
export const IconScheduleTick = icon(SvgCheck, 16);
export const IconScheduleField = icon(SvgCalendar, 19);
export const IconScheduleClear = icon(SvgClose, 16);
export const IconScheduledClock = icon(SvgClock, 16);
export const IconScheduledEdit = icon(SvgPencil, 16);
export const IconEditConfirm = icon(SvgCheck, 22);
export const IconScheduledSendNow = icon(SvgSend, 16);
export const IconScheduledReschedule = icon(SvgSendClock, 16);
export const IconScheduledDelete = icon(SvgTrash3, 16);

/* FR71 Screen 71.2 — the contact info rows. */

export const IconBell = icon(SvgBell, 21);
export const IconPhoto = icon(SvgImage, 21);
export const IconPalette = icon(SvgColor, 21);
export const IconFontSize = icon(SvgTextSize, 21);
export const IconDownload = icon(SvgDownload, 21);
export const IconPin = icon(SvgPin, 21);
export const IconBookmark = icon(SvgBookmark, 21);
export const IconGroupMembers = icon(SvgUsers, 21);
export const IconTimer = icon(SvgClock, 21);
export const IconEditChat = icon(SvgPencil, 21);
export const IconExitGroup = icon(SvgLogOut, 21);
export const IconRemoveFromGroup = icon(SvgUserMinus, 17);
export const IconMemberSearch = icon(SvgSearch, 18);

/* Message footer marks, sized to sit beside the timestamp. */
export const IconPinMark = icon(SvgPin, 14);
export const IconStarMark = icon(SvgStar, 14);
export const IconKeepMark = icon(SvgBookmark, 14);
export const IconTickSent = icon(SvgCheck, 16);
export const IconTickDelivered = icon(SvgCheckDouble, 16);

/* Chat list row indicators, smaller than the menu items they mirror. */
export const IconPinIndicator = icon(SvgPin, 14);
export const IconMutedIndicator = icon(SvgBellOff, 15);

/* Recent chat context menu, sized to match IconBlock already in that menu. */
export const IconUnarchiveLine = icon(SvgUnarchive, 18);
export const IconBellOff = icon(SvgBellOff, 18);
export const IconMarkUnread = icon(SvgDisc, 18, 'icon-disc-filled');
export const IconMarkRead = icon(SvgCheckDouble, 18);
export const IconLogOut = icon(SvgLogOut, 18);
export const IconArchive = icon(SvgArchive, 18);
export const IconStar = icon(SvgStar, 21);
export const IconSearchGlass = icon(SvgSearch, 21);
export const IconLockClosed = icon(SvgLock, 21);
export const IconBroom = icon(SvgEraser, 21);
export const IconTrash = icon(SvgTrash, 21);
export const IconFlag = icon(SvgFlag, 21);

export const IconUnpin = icon(SvgUnpin, 16);
export const IconUnstar = icon(SvgStarOff, 16);
export const IconUnkeep = icon(SvgBookmarkOff, 16);

/* Action menus -- the message menu and the recent-chat one, which the mock draws with
   the same glyphs at the same size, so the labels line up in both. */
// The same glyph the toolbar's Reply button uses, so the two read as one action.
export const IconMenuReply = icon(SvgReply, 18);
export const IconMenuForward = icon(SvgCornerUpRight, 18);
export const IconMenuStar = icon(SvgStar, 18);
export const IconMenuUnstar = icon(SvgStarOff, 18);
export const IconMenuPin = icon(SvgPin, 18);
export const IconMenuUnpin = icon(SvgUnpin, 18);
export const IconMenuKeep = icon(SvgBookmark, 18);
export const IconMenuUnkeep = icon(SvgBookmarkOff, 18);
export const IconMenuCopy = icon(SvgCopy, 18);
export const IconMenuInfo = icon(SvgInfo, 18);
export const IconMenuDownload = icon(SvgDownload, 18);
export const IconMenuEdit = icon(SvgPencil, 18);
export const IconMenuReport = icon(SvgFlag2, 18);
// The mock's bin carries two inner strokes that the plainer library bins leave out.
export const IconMenuDelete = icon(SvgTrash3, 18);

/* Dialog close button. */
export const IconClose = icon(SvgClose, 20);

/* Reply bar above the composer (FR92). */
export const IconReplyCancel = icon(SvgClose, 16);

/* Share a contact and failed sends (FR119) ----------------------------------- */

export const IconTrayRemove = icon(SvgClose, 12);
export const IconSendFailed = icon(SvgAlertCircle, 14);

/* Attachment menu (FR71) ---------------------------------------------------- */

export const IconDocument = icon(SvgFile, 15);
export const IconPoll = icon(SvgBarChart, 15);

/* Poll ----------------------------------------------------------------------- */

export const IconPollGrip = icon(SvgGrip, 18);
export const IconPollOptionRemove = icon(SvgClose, 15);
export const IconPollNoDeadline = icon(SvgClock, 16);
export const IconPollDeadline = icon(SvgCalendar, 16);
export const IconPollDeadlineField = icon(SvgCalendar, 19);
export const IconPollLock = icon(SvgLock, 13);
export const IconPollClosedNote = icon(SvgLock, 16);
export const IconPollMenuClose = icon(SvgLock, 18);
export const IconPickerPrevious = icon(SvgChevronLeft, 16);
export const IconPollReact = icon(SvgSmile, 17);

/* Location pop-up map controls (FR120) --------------------------------------- */

export const IconMapZoomIn = icon(SvgPlus, 18);
export const IconMapZoomOut = icon(SvgMinus, 18);
export const IconMapRecentre = icon(SvgLocation, 19);

/* Voice message (FR71) ------------------------------------------------------ */

export const IconPlay = icon(SvgPlay, 16);
export const IconPause = icon(SvgPause, 16);
export const IconStopRecord = icon(SvgStop, 15);
export const IconMic = icon(SvgMic, 17);
export const IconSend = icon(SvgSend, 20);

/* Chat list preview (FR59) --------------------------------------------------
 *
 * One size for the whole line, so a row of mixed media types keeps a single
 * optical weight. The glyphs paint with currentColor, letting a call preview
 * tint its icon along with its wording. */

export const IconPreviewPhoto = icon(SvgImage, 15);
export const IconPreviewVideo = icon(SvgVideo, 15);
export const IconPreviewVoice = icon(SvgMic, 15);
export const IconPreviewDocument = icon(SvgFile, 15);
export const IconPreviewLocation = icon(SvgMapPin, 15);
export const IconPreviewLiveLocation = icon(SvgRadio, 15);
export const IconPreviewContact = icon(SvgUser, 15);

// A call preview marks the kind of call, not its outcome: the state is carried by the
// wording and the colour. The call log bubble uses plated badges instead.
export const IconPreviewVoiceCall = icon(SvgPhone, 15);
export const IconPreviewVideoCall = icon(SvgVideo, 15);

// Pending delivery. Sized with the ticks it stands in for, not with the glyphs above.
export const IconPreviewPending = icon(SvgClock, 14);

/* View once card -- the media type the locked item holds. */

export const IconViewOncePhoto = icon(SvgImage, 17);
export const IconViewOnceVideo = icon(SvgVideo, 17);
export const IconViewOnceVoice = icon(SvgMic, 17);

/* Sync loading screen. */

export const IconSyncLock = icon(SvgLock, 15);

/* Reactions (FR93): the quick bar's "+" and the picker's search and category tabs. */

export const IconReactMore = icon(SvgPlus, 20);
export const IconPickerSearch = icon(SvgSearch, 16);
export const IconPickerRecent = icon(SvgClock, 19);
export const IconPickerSmileys = icon(SvgSmile, 19);
export const IconPickerObjects = icon(SvgBulb, 19);
export const IconPickerFlags = icon(SvgFlag, 19);
